import { LoaderFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { client } from '../../../../../gql/client';
import { FETCH_PREDEFINED_OPTIONS_ACCIDENTS } from '../../../../../gql/queries/accidents/predefinedData';
import { selectBasicAccidentInformation } from '../../../../../gql/selectors/accidents';

// Utils
import { formAccidentNaming } from '../../../../../components/forms/AddBasicInfoAccidentForm/constants';
import { AuthContextType } from '../../../../../hooks/useAuth';
import { claimDraftScope } from '../../../../../utils/draftOwnership';
import {
  ACCIDENT_EDIT_SCOPE,
  ACCIDENT_MEDIA_SCOPE,
} from '../../../../../utils/draftScopes';
import { readAccidentEditCopy } from '../../../../../utils/accidentEditDraft';
//TODO: la estructura parece que esta bien sin no mirar pantallazos pero no se esta rellenando los datos para que se representem bien por eso creo que se buguea

// Types
import { type Accident } from '../../../../../types/accident';

const loadBasicInformationSummary = async (idOrder: string) => {
  try {
    const workOrderData = await selectBasicAccidentInformation(idOrder);

    return workOrderData;
  } catch (err) {
    Sentry.captureException(err);
    console.log('> Work Order Summary loading error:', err);
    return true;
  }
};

const loadFormOptions = async (userData: AuthContextType | null) => {
  try {
    if (userData?.user) {
      const { data } = await client.query({
        query: FETCH_PREDEFINED_OPTIONS_ACCIDENTS,
      });

      return {
        ...data,
      };
    }
    return {};
  } catch (err) {
    Sentry.captureException(err);
    console.log('> Accident Summary loading error:', err);
    return true;
  }
};

export const EditBasicInfoAccidentLoader: (
  userData: AuthContextType | null
) => LoaderFunction | undefined = (userData) => async ({ params }) => {
  // 🔴 Antes de cualquier `await`: borradores y croquis del accidente de la
  // URL (ver el loader del paso 1). Este paso ni siquiera miraba la URL:
  // sacaba la Order de la copia del paso 1, así que con atrás la URL de A
  // enseñaba B y su «Continuar» seguía en B (R5).
  claimDraftScope(ACCIDENT_EDIT_SCOPE, params.id);
  claimDraftScope(ACCIDENT_MEDIA_SCOPE, params.id);

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
    // La Order es la de la URL, nunca la de la copia.
    const orderParentId = params.id;

    // Las copias se leen antes de esperar nada y solo si son de esta Order.
    const sessionStorageData = readAccidentEditCopy<Partial<Accident>>(
      formAccidentNaming.EDIT_BASIC_INFO_ACCIDENT_FORM,
      orderParentId
    );
    const accidentData = readAccidentEditCopy<Accident>(
      formAccidentNaming.EDIT_ACCIDENT_FORM,
      orderParentId
    );

    // Sin el paso 1 de ESTA Order no hay con qué guardar (el guardado lee su
    // copia): la pantalla vuelve al paso 1 (`null` → `navigate('..')`).
    if (!orderParentId || !accidentData) {
      return null;
    }

    const orderParentRoid = accidentData.roid;
    const registerInitDate = accidentData.registerInitDate;
    const road = accidentData.road;

    const loaderToResolve = await loadFormOptions(userData);

    const dataFromBasicInformation = await loadBasicInformationSummary(
      orderParentId
    );

    if (sessionStorageData && userData && orderParentRoid) {
      const combinedData: Accident = {
        orderParentRoid,
        orderParentId,
        road,
        registerInitDate,
        ...loaderToResolve,
        ...(dataFromBasicInformation as object),
        ...sessionStorageData,
      };

      return combinedData;
    }

    return {
      orderParentRoid,
      orderParentId,
      registerInitDate,
      road,
      ...loaderToResolve,
      dataToCompare: dataFromBasicInformation,
      ...(dataFromBasicInformation as object),
    };
  };

  return { data: load() };
};
